import React from "react";
import moment from "moment-timezone";
import { hideModal } from "../../util/FormatUtil";
import SamplesAPI from "../../network/SamplesAPI";
import { ReportDownloadHistory, REPORT_TYPE_LABELS } from "../../types/ReportDownload";

interface ReportDownloadHistoryModalProps {
    reqNum: string
    // bump to force a re-fetch for the same reqNum (e.g. after a new download)
    refreshKey?: number
    zIndex?: number
}

interface ReportDownloadHistoryModalState {
    history: ReportDownloadHistory[]
    loading: boolean
    error: string
}

// Popup on the Samples page showing who downloaded a sample's PDF reports and when.
export class ReportDownloadHistoryModal extends React.Component<ReportDownloadHistoryModalProps, ReportDownloadHistoryModalState> {
    public static ID = "reportdownloadhistory";

    constructor(props) {
        super(props);
        this.state = { history: [], loading: false, error: "" };
    }

    componentDidUpdate(prevProps: Readonly<ReportDownloadHistoryModalProps>) {
        if (this.props.reqNum && (prevProps.reqNum !== this.props.reqNum || prevProps.refreshKey !== this.props.refreshKey)) {
            this.loadHistory();
        }
    }

    loadHistory = async () => {
        const reqNum = this.props.reqNum;
        this.setState({ loading: true, error: "", history: [] });
        try {
            const response = await SamplesAPI.getReportDownloadHistory(reqNum);
            // ignore a stale response if the user opened another sample meanwhile
            if (reqNum !== this.props.reqNum) {
                return;
            }
            if (!response.success) {
                return this.setState({ loading: false, error: response.reason || "Could not load download history." });
            }
            this.setState({ loading: false, history: response.data || [] });
        } catch (e) {
            console.error(e);
            this.setState({ loading: false, error: "Could not load download history." });
        }
    };

    buildBody() {
        if (this.state.loading) {
            return <div className="p-3">Loading...</div>;
        }
        if (this.state.error) {
            return <div className="p-3 text-danger">{this.state.error}</div>;
        }
        if (this.state.history.length === 0) {
            return <div className="p-3">No reports have been downloaded for this sample.</div>;
        }
        const tz = moment.tz.guess();
        return (
            <div className="table-responsive">
                <table className="table table-striped mb-0">
                    <thead>
                        <tr>
                            <th scope="col">Date/Time</th>
                            <th scope="col">User</th>
                            <th scope="col">Report</th>
                            <th scope="col">Method</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.history.map(h => (
                            <tr key={h.ID}>
                                <td>{moment.tz(h.DownloadedDate, tz).format("MM/DD/YYYY h:mm A")}</td>
                                <td>{h.UserEmail || "Unknown"}</td>
                                <td>{REPORT_TYPE_LABELS[h.ReportType] || h.ReportType}</td>
                                <td>{h.DownloadMethod === "Bulk" ? "Bulk download" : "View Report"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        return (
            <div className="modal fade form_modal" id={ReportDownloadHistoryModal.ID} tabIndex={-1}
                style={{ zIndex: this.props.zIndex }} role="dialog"
                aria-labelledby="report_download_history_label"
                aria-hidden="true">
                <div className="modal-dialog modal-xl" style={{ maxWidth: "1000px" }} role="document">
                    <div className="modal-content">
                        <div className="card border-0">
                            <div className="card-header verlag-bold">
                                <h4 id="report_download_history_label">
                                    Report View History {this.props.reqNum ? `- ${this.props.reqNum}` : ""}
                                    <button style={{ outline: 'none' }} type="button"
                                        className="close pr-4"
                                        aria-label="Close"
                                        onClick={() => hideModal(ReportDownloadHistoryModal.ID)}>
                                        <span aria-hidden="true" style={{ fontSize: '1.5em', fontWeight: 'bold' }}>&times;</span>
                                    </button>
                                </h4>
                            </div>
                            <div className="card-body p-0">
                                {this.buildBody()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
